import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { displayToastError } from '@/helpers';
import { sendPasswordResetEmail } from '@/lib/auth';

import { resetPasswordFormSchema } from '../validation/reset-password-form-schema';

interface ResetPasswordFormValues {
  email: string;
}

interface ResetPasswordFormProps {
  onSuccess: () => void;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation('unauthenticated.resetPassword');
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: async (input: ResetPasswordFormValues) => {
      await sendPasswordResetEmail(input.email);
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      displayToastError(error.message);
    },
  });

  const onSubmit = (values: ResetPasswordFormValues) => {
    mutation.mutate(values);
  };

  return (
    <div className="flex w-full max-w-sm flex-col">
      <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('header')}</h1>
      <p className="mb-6 text-sm text-muted-foreground">{t('subtitle')}</p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('emailLabel')}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t('emailPlaceholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <ButtonLoadable type="submit" className="w-full" isLoading={mutation.isPending}>
            {t('sendResetLink')}
          </ButtonLoadable>
        </form>
      </Form>
    </div>
  );
};
